
import { useState, useEffect } from 'react';
import { NodeViewProps, NodeViewWrapper } from '@tiptap/react';
import { Button } from '@/components/ui/button';
import ImageDropdownMenu from './ImageDropdownMenu';

export default function ImageComponent({ node, getPos, editor }: NodeViewProps) {
  const [src, setSrc] = useState<string>(node.attrs.src || '');
  const [alt, setAlt] = useState<string>(node.attrs.alt || 'Image');
  const [width, setWidth] = useState<string>(node.attrs.width || '100%');
  const [alignment, setAlignment] = useState<'left' | 'center' | 'right'>(
    node.attrs.alignment || 'center'
  );
  const [caption, setCaption] = useState<string>(node.attrs.caption || '');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);

  // Utility to get width as a number for the slider
  const getWidthAsNumber = (): number => {
    if (typeof width === 'string' && width.endsWith('%')) {
      return parseInt(width);
    }
    return 100;
  };

  useEffect(() => {
    setSrc(node.attrs.src || '');
    setAlt(node.attrs.alt || 'Image');
    setWidth(node.attrs.width || '100%');
    setAlignment(node.attrs.alignment || 'center');
    setCaption(node.attrs.caption || '');
  }, [node.attrs]);

  const updateAttributes = (attrs: Record<string, any>) => {
    if (!editor || typeof getPos !== 'function') return;
    const pos = getPos();
    editor
      .chain()
      .focus()
      .command(({ tr }) => {
        tr.setNodeMarkup(pos, undefined, {
          ...node.attrs,
          ...attrs
        });
        return true;
      })
      .run();
  };

  const onResizeEnd = (value: number[]) => {
    const newWidth = `${value[0]}%`;
    setWidth(newWidth);
    updateAttributes({ width: newWidth });
  };

  const handleAlignmentChange = (newAlignment: 'left' | 'center' | 'right') => {
    setAlignment(newAlignment);
    updateAttributes({ alignment: newAlignment });
  };

  const handleCaptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCaption(e.target.value);
  };

  const handleSetCaption = () => {
    updateAttributes({ caption });
  };

  const handleDelete = () => {
    if (!editor || typeof getPos !== 'function') return;
    editor
      .chain()
      .focus()
      .command(({ tr }) => {
        tr.delete(getPos(), getPos() + node.nodeSize);
        return true;
      })
      .run();
  };

  const handleImageLoad = () => {
    setIsLoading(false);
    setHasError(false);
  };

  const handleImageError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  const getContainerStyles = () => {
    const styles: Record<string, string> = {
      width,
    };

    switch (alignment) {
      case 'left':
        styles.marginRight = 'auto';
        styles.marginLeft = '0';
        break;
      case 'right':
        styles.marginLeft = 'auto';
        styles.marginRight = '0';
        break;
      case 'center':
      default:
        styles.marginLeft = 'auto';
        styles.marginRight = 'auto';
        break;
    }

    return styles;
  };

  return (
    <NodeViewWrapper className="relative my-4">
      <div className="image-container" style={getContainerStyles()}>
        {isLoading && (
          <div className="w-full aspect-video bg-black/20 animate-pulse flex items-center justify-center rounded-md">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-white"></div>
          </div>
        )}

        {hasError ? (
          <div className="w-full bg-red-500/20 p-4 rounded-md flex flex-col items-center justify-center">
            <p className="text-sm text-center text-red-300">Failed to load image</p>
            <Button
              variant="destructive"
              size="sm"
              className="mt-2"
              onClick={handleDelete}
              type="button"
            >
              Remove Image
            </Button>
          </div>
        ) : (
          <div className="relative rounded-md overflow-hidden">
            <img
              src={src}
              alt={alt}
              title={node.attrs.title}
              className={`w-full h-auto object-contain rounded-md ${isLoading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-200`}
              onLoad={handleImageLoad}
              onError={handleImageError}
              style={{ display: isLoading ? 'none' : 'block' }}
            />

            {!isLoading && !hasError && (
              <div className="absolute top-2 right-2 z-10">
                <ImageDropdownMenu
                  alignment={alignment}
                  onAlignmentChange={handleAlignmentChange}
                  width={getWidthAsNumber()}
                  onWidthChange={(val) => setWidth(`${val[0]}%`)}
                  onWidthCommit={onResizeEnd}
                  onDelete={handleDelete}
                  caption={caption}
                  onCaptionChange={handleCaptionChange}
                  onSetCaption={handleSetCaption}
                />
              </div>
            )}
          </div>
        )}

        {caption && !hasError && !isLoading && (
          <figcaption className="text-center text-sm text-gray-400 mt-1 italic">
            {caption}
          </figcaption>
        )}
      </div>
    </NodeViewWrapper>
  );
}
